// eslint-disable-next-line import/named
import { EventInput } from '@fullcalendar/core';
import { BookingCompressed, BookingCompressedVisibilityEnum } from '@jooxter/api';
import { fromISO, JxtEventColorInfos, JxtEventColorsEnum } from '@jooxter/utils';
import { Adapter, EventTypeEnum, TAdapterOptions } from '../types';
import {
  displayOwnerValidationStatus,
  displayOwnerValidationWaiting,
  getCheckout,
  isBookingStartLocked,
} from '@jooxter/bookings';

export const BookingCompressedListItemAdapter: Adapter<BookingCompressed, EventInput> = {
  adapt(booking: BookingCompressed, options?: TAdapterOptions): EventInput {
    const start = fromISO(booking.start.dateTime);
    let end = fromISO(booking.end.dateTime);

    const getEventTitle = () => {
      switch (options?.title) {
        case 'organizer':
          return `${booking.organizer.firstname} ${booking.organizer.lastname}`;
        case 'summary':
          return booking.summary;
        default:
          return booking.resource.name;
      }
    };

    const isValidated = displayOwnerValidationStatus(booking);
    const isPending = displayOwnerValidationWaiting(booking);

    if (booking.checkout?.at) {
      end = getCheckout(booking as Omit<BookingCompressed, 'checkout'> & { checkout: { at: string } });
    }

    return {
      id: booking.id.toString(),
      title: getEventTitle(),
      start: start.toJSDate(),
      end: end.toJSDate(),
      backgroundColor: JxtEventColorInfos[JxtEventColorsEnum.parse(booking.color)].bgColor,
      borderColor: JxtEventColorInfos[JxtEventColorsEnum.parse(booking.color)].borderColor,
      textColor: JxtEventColorInfos[JxtEventColorsEnum.parse(booking.color)].textColor,
      constraint: 'bgClosedId',
      editable: options?.editable && booking.capabilities?.update,
      // Dragging moves the start: a locked start leaves only the resize of the end.
      ...(isBookingStartLocked(booking, options?.userType) && { startEditable: false }),
      groupId: booking.id.toString(),
      extendedProps: {
        type: EventTypeEnum.BOOKING,
        isValidated: isValidated,
        isPending: isPending,
        nbAttendees: booking.physicalAttendees,
        capabilities: booking.capabilities,
        color: JxtEventColorsEnum.parse(booking.color),
        originalBooking: booking,
        icons: booking.icons?.filter((icon) => !!icon),
        organizer: booking.organizer,
        resourceName: booking.resource.name,
        floorId: booking.resource.floorId,
        isPrivate: booking.visibility === BookingCompressedVisibilityEnum.Private,
        physicalAttendees: booking.physicalAttendees,
        participationStatus: booking.participationStatus,
        resourceId: booking.resource.id,
        resourceTypeId: booking.resource.resourceTypeId,
        rrule: booking.rrule,
      },
      resourceId: booking.resource.id.toString(),
    };
  },

  revert() {
    throw new Error('Method not implemented');
  },
};
